import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { Button, Input, SegmentedControl } from '@column/column-ui-kit';
import { PopoverBase, PopoverBaseProps } from '../Base';
import { PopoverAction } from '../List';
import { FormElement, FormLabel } from '~/styles';
import { DateInput } from '~/components/DateInput';

export interface PopoverFilterTransferDateData {
  createdAt: string;
  createdAtTime: number;
  createdAtTimeAM: boolean;
}

interface PopoverFilterTransferStatusProps extends PopoverBaseProps {
  data: Partial<PopoverFilterTransferDateData>;
  onSubmit?: (data: Partial<PopoverFilterTransferDateData>) => void;
}

const Wrapper = styled(PopoverBase)`
  --popover-padding: 0px;
  --popover-width: 248px;
  --popover-left: 0px;
`;

const Grid = styled.div`
  display: grid;
  grid-gap: 16px;
  padding: 12px 12px 0 12px;
`;

const TimeWrapper = styled.div`
  display: grid;
  grid-template-columns: auto 83px;
  grid-gap: 8px;
  align-items: center;
`;

const StyledSegmentedControl = styled(SegmentedControl)`
  button {
    padding: 5px 8px;
  }
`;

export const PopoverFilterTransferDate: React.FC<PopoverFilterTransferStatusProps> = ({
  data,
  onSubmit,
  ...popoverProps
}) => {
  const [currentData, setCurrentData] = useState<Partial<PopoverFilterTransferDateData>>(data ?? {});

  const handleResetClick = () => {
    setCurrentData({});

    if (onSubmit) {
      onSubmit({});
    }
  };

  const handleApplyClick = () => {
    if (onSubmit) {
      onSubmit(currentData);
    }
  };

  useEffect(() => {
    setCurrentData(data);
  }, [data, popoverProps.show]);

  const isValidTime = useMemo(() => {
    if (typeof currentData.createdAtTime === 'undefined' || !currentData.createdAtTime) {
      return true;
    }

    if (currentData.createdAtTime.toString().length !== 4) {
      return false;
    }

    const numberValue = Number(currentData.createdAtTime);

    return numberValue >= 0 && numberValue <= 1200;
  }, [currentData.createdAtTime]);

  const isDisabled = useMemo(() => {
    if (currentData.createdAtTime && currentData.createdAtTime.toString().length > 0 && !isValidTime) {
      return true;
    }

    return false;
  }, [currentData.createdAtTime, isValidTime]);

  return (
    <Wrapper {...popoverProps}>
      <Grid>
        <FormElement>
          <FormLabel>Created at</FormLabel>
          <DateInput
            size="small"
            value={currentData.createdAt}
            onChange={(value) => setCurrentData((s) => ({ ...s, createdAt: value }))}
          />
        </FormElement>
        <FormElement>
          <FormLabel>Time</FormLabel>
          <TimeWrapper>
            <Input
              placeholder="00:00"
              mask="##:##"
              value={currentData.createdAtTime?.toString()}
              onChange={(value) => setCurrentData((s) => ({ ...s, createdAtTime: Number(value) }))}
              hasError={!isValidTime}
              size="small"
            />
            <StyledSegmentedControl
              options={[
                {
                  label: 'AM',
                  value: true,
                },
                {
                  label: 'PM',
                  value: false,
                },
              ]}
              size="small"
              active={currentData.createdAtTimeAM ?? true}
              onOptionChange={(value: boolean | undefined) => setCurrentData((s) => ({ ...s, createdAtTimeAM: value }))}
            />
          </TimeWrapper>
        </FormElement>
      </Grid>
      <PopoverAction>
        <Button size="small" variant="secondary" onClick={handleResetClick}>
          Reset
        </Button>
        <Button size="small" onClick={handleApplyClick} isDisabled={isDisabled}>
          Apply
        </Button>
      </PopoverAction>
    </Wrapper>
  );
};
